/**
 * Completion checker for radial (polar) mazes.
 *
 * Determines whether the user's drawn path connects the entrance
 * (outermost ring) to the exit (centre cell) through continuous segments.
 */

import type { RadialMazeState, PathSegment, Position } from '../types/maze-types';
import { cartesianToPolar } from './polarUtils';

const TWO_PI = 2 * Math.PI;

/**
 * Identify which radial cell (ring, sector) a cartesian point belongs to.
 * Returns null if the point is outside the maze boundary.
 */
function pointToCell(
  point: Position,
  state: RadialMazeState,
): { ring: number; sector: number } | null {
  const { center, ringWidth, rings, sectorsPerRing } = state;
  const polar = cartesianToPolar(point.x, point.y, center.x, center.y);

  // Centre cell (ring 0) — within first ringWidth
  if (polar.r < ringWidth) {
    return { ring: 0, sector: 0 };
  }

  const ring = Math.min(Math.floor(polar.r / ringWidth), rings - 1);
  if (ring < 0 || ring >= rings) return null;

  const sectorCount = sectorsPerRing[ring];
  const sectorAngle = TWO_PI / sectorCount;
  let sector = Math.floor(polar.theta / sectorAngle);
  if (sector >= sectorCount) sector = sectorCount - 1;

  return { ring, sector };
}

/**
 * Check whether the user's path connects the entrance cell to the exit cell
 * in a radial maze.
 *
 * Algorithm:
 * 1. Map each path point to its radial cell (ring, sector).
 * 2. Build a connectivity graph between consecutive points within segments
 *    and between nearby endpoints across segments.
 * 3. BFS from any entrance-cell point to any exit-cell point.
 */
export function checkRadialMazeCompletion(
  pathSegments: PathSegment[],
  state: RadialMazeState,
): boolean {
  if (!pathSegments || pathSegments.length === 0) return false;

  const { entrance, exit, ringWidth } = state;

  // Step 1: Collect all points with their cell assignments
  interface PointInfo {
    x: number;
    y: number;
    ring: number;
    sector: number;
    cellKey: string;
  }

  const allPoints: PointInfo[] = [];

  for (const segment of pathSegments) {
    for (const point of segment.points) {
      const cell = pointToCell(point, state);
      if (cell) {
        allPoints.push({
          x: point.x,
          y: point.y,
          ring: cell.ring,
          sector: cell.sector,
          cellKey: `${cell.ring},${cell.sector}`,
        });
      }
    }
  }

  if (allPoints.length === 0) return false;

  // Step 2: Build connectivity graph
  const graph = new Map<number, Set<number>>();

  const addEdge = (a: number, b: number) => {
    if (!graph.has(a)) graph.set(a, new Set());
    if (!graph.has(b)) graph.set(b, new Set());
    graph.get(a)!.add(b);
    graph.get(b)!.add(a);
  };

  // Connect consecutive points within each segment
  let idx = 0;
  for (const segment of pathSegments) {
    const start = idx;
    const end = idx + segment.points.length;
    for (let i = start; i < end - 1; i++) {
      // Only connect if both points mapped to valid cells
      if (i < allPoints.length && i + 1 < allPoints.length) {
        addEdge(i, i + 1);
      }
    }
    idx = end;
  }

  // Connect endpoints across different segments if they're close enough
  const connectionThreshold = ringWidth * 0.6;

  const segmentBounds: Array<{ start: number; end: number }> = [];
  let si = 0;
  for (const segment of pathSegments) {
    segmentBounds.push({ start: si, end: si + segment.points.length });
    si += segment.points.length;
  }

  const getSegmentIndex = (ptIdx: number): number => {
    for (let s = 0; s < segmentBounds.length; s++) {
      if (ptIdx >= segmentBounds[s].start && ptIdx < segmentBounds[s].end) return s;
    }
    return -1;
  };

  // Collect segment endpoints
  const endpoints: number[] = [];
  for (const bound of segmentBounds) {
    if (bound.end - bound.start > 0) {
      endpoints.push(bound.start);
      if (bound.end - bound.start > 1) {
        endpoints.push(bound.end - 1);
      }
    }
  }

  for (let i = 0; i < allPoints.length; i++) {
    for (const ep of endpoints) {
      if (i === ep) continue;
      if (getSegmentIndex(i) === getSegmentIndex(ep)) continue;

      const dx = allPoints[i].x - allPoints[ep].x;
      const dy = allPoints[i].y - allPoints[ep].y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= connectionThreshold) {
        addEdge(i, ep);
      }
    }
  }

  // Step 3: Find entrance and exit points
  const entranceKey = `${entrance.ring},${entrance.sector}`;
  const exitKey = `${exit.ring},${exit.sector}`;

  const entrancePoints = new Set<number>();
  const exitPoints = new Set<number>();

  for (let i = 0; i < allPoints.length; i++) {
    if (allPoints[i].cellKey === entranceKey) entrancePoints.add(i);
    if (allPoints[i].cellKey === exitKey) exitPoints.add(i);
  }

  // Also treat points just outside the entrance (in the entrance gap) as entrance points
  // The entrance has its outward wall removed, so points slightly beyond outermost ring count
  for (let i = 0; i < allPoints.length; i++) {
    if (entrancePoints.has(i)) continue;
    const pt = allPoints[i];
    // Check if point is in the outward extension of the entrance sector
    if (pt.ring === entrance.ring) {
      const polar = cartesianToPolar(
        allPoints[i].x, allPoints[i].y,
        state.center.x, state.center.y,
      );
      if (polar.r >= entrance.ring * state.ringWidth) {
        const sectorCount = state.sectorsPerRing[entrance.ring];
        const sectorAngle = TWO_PI / sectorCount;
        const sectorStart = entrance.sector * sectorAngle;
        const sectorEnd = (entrance.sector + 1) * sectorAngle;
        if (polar.theta >= sectorStart && polar.theta <= sectorEnd) {
          entrancePoints.add(i);
        }
      }
    }
  }

  if (entrancePoints.size === 0 || exitPoints.size === 0) return false;

  // Step 4: BFS from entrance to exit
  for (const startPt of entrancePoints) {
    const visited = new Set<number>();
    const queue = [startPt];
    visited.add(startPt);

    if (!graph.has(startPt)) continue;

    while (queue.length > 0) {
      const current = queue.shift()!;

      if (exitPoints.has(current)) return true;

      const neighbors = graph.get(current);
      if (neighbors) {
        for (const neighbor of neighbors) {
          if (!visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push(neighbor);
          }
        }
      }
    }
  }

  return false;
}
